import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  Building2,
  Check,
  XCircle,
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  Shield,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  clinicOnboardingService,
  ClinicOnboardingInviteDetails,
  CompleteClinicOnboardingRequest,
} from '@/services/clinicOnboardingService';
import { clinicOnboardingBillingService } from '@/services/clinicOnboardingBillingService';
import { stateService } from '@/services/stateService';
import { State } from '@/models/state.model';
import { useAuth } from '@/contexts/AuthContext';
import { clearActiveClinicId } from '@/contexts/ClinicContext';
import {
  ClinicInfoStep,
  OwnerInfoStep,
  InvitePractitionersStep,
  AcknowledgementStep,
  BillingStep,
  StripeOnboardingStep,
} from './clinic-onboarding/steps';
import { FULL_ONBOARDING_STEPS, getVisibleSteps } from './clinic-onboarding/stepRegistry';
import { ClinicOnboardingData } from './clinic-onboarding/types';
import { getFooterText } from '@/config/version';

/** Handle exposed by BasicInfoStep for parent-triggered validation (kept for backward compat) */
export interface BasicInfoStepHandle {
  triggerValidation: () => Promise<boolean>;
}

const ClinicOnboarding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState('');
  const [inviteDetails, setInviteDetails] = useState<ClinicOnboardingInviteDetails | null>(null);
  const [states, setStates] = useState<State[]>([]);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [onboardingToken, setOnboardingToken] = useState<string | null>(null);
  const clinicInfoRef = useRef<{ triggerValidation: () => Promise<boolean> }>(null);
  const ownerInfoRef = useRef<{ triggerValidation: () => Promise<boolean> }>(null);

  const [data, setData] = useState<ClinicOnboardingData>({
    name: '',
    npi: '',
    email: '',
    primary_phone: '',
    fax: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    description: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    password: '',
    confirm_password: '',
    add_as_practitioner: false,
    use_clinic_address_for_practitioner: true,
    skipPaymentSetup: true,
    practitionersToInvite: [],
    paymentData: undefined,
  });

  const token = searchParams.get('token');

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await stateService.getAllStates();
        if (response.data?.states) {
          setStates(response.data.states);
        }
      } catch (error) {
        console.error('Failed to fetch states:', error);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidationError('No invitation token provided. Please use the link from your email.');
        setIsValidating(false);
        return;
      }

      try {
        const response = await clinicOnboardingService.validateInvite(token);
        if (response.success) {
          const d = response.data;
          if (d.onboardingType === 'partial') {
            navigate(`/onboard/clinic/complete?token=${encodeURIComponent(token)}`, { replace: true });
            return;
          }
          setInviteDetails(d);
          setData(prev => ({
            ...prev,
            name: d.clinicName,
            email: d.email,
            admin_email: d.email,
            admin_first_name: d.existingUser?.firstName || prev.admin_first_name,
            admin_last_name: d.existingUser?.lastName || prev.admin_last_name,
            clinicArchetype: d.clinicArchetype || prev.clinicArchetype,
          }));
          setIsValidating(false);
        }
      } catch (error: any) {
        console.error('Token validation failed:', error);
        const errorMessage = error?.response?.data?.error?.message || 'Invalid or expired invitation token';
        setValidationError(errorMessage);
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const updateData = useCallback((updates: Partial<ClinicOnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const effectiveArchetype = data.clinicArchetype ?? inviteDetails?.clinicArchetype;
  const visibleSteps = getVisibleSteps(FULL_ONBOARDING_STEPS, effectiveArchetype ?? null);

  useEffect(() => {
    if (currentStep >= visibleSteps.length) {
      setCurrentStep(Math.max(0, visibleSteps.length - 1));
    }
  }, [effectiveArchetype, visibleSteps.length, currentStep]);

  const runAutoAuth = useCallback(async () => {
    const email = data.admin_email || inviteDetails?.email;
    const password = data.password;
    if (!email || !password) {
      navigate('/login', { state: { email: email || '' } });
      return;
    }
    try {
      localStorage.removeItem('selectedClinicId');
      localStorage.removeItem('selectedClinicTimestamp');
      clearActiveClinicId();
      await login(email, password);
      navigate('/', { replace: true });
    } catch {
      navigate('/login', { state: { email } });
    }
  }, [data.admin_email, data.password, inviteDetails?.email, login, navigate]);

  const handleNext = async () => {
    if (currentStep >= visibleSteps.length - 1) return;

    const stepId = visibleSteps[currentStep]?.id;

    if (stepId === 'clinic-info' && clinicInfoRef.current) {
      const isValid = await clinicInfoRef.current.triggerValidation();
      if (!isValid) {
        toast({ title: 'Missing Information', description: 'Please fill in all required clinic fields.', variant: 'destructive' });
        setTimeout(() => {
          document.querySelector('[data-invalid="true"], .text-destructive, [aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }
    }

    if (stepId === 'owner-info' && ownerInfoRef.current) {
      const isValid = await ownerInfoRef.current.triggerValidation();
      if (!isValid) {
        toast({ title: 'Missing Information', description: 'Please fill in all required owner fields.', variant: 'destructive' });
        setTimeout(() => {
          document.querySelector('[data-invalid="true"], .text-destructive, [aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }
    }

    if (stepId === 'acknowledgement') {
      if (!isAcknowledged && !data.programAcknowledged) {
        toast({ title: 'Agreement required', description: 'Please read the agreement and click "I Acknowledge & Agree" to continue.', variant: 'destructive' });
        return;
      }
      if (!token) {
        toast({ title: 'Error', description: 'Invalid invitation token', variant: 'destructive' });
        return;
      }
      setIsSubmitting(true);
      try {
        const selectedState = states.find((s) => s.code === data.state);
        const isGuided = effectiveArchetype === 'guided';
        const shouldAddPractitioner = !isGuided && Boolean(data.add_as_practitioner);
        const payload: CompleteClinicOnboardingRequest = {
          token,
          name: data.name,
          npi: data.npi,
          address_line_1: data.address_line_1,
          address_line_2: data.address_line_2 || undefined,
          city: data.city,
          state: selectedState?.id?.toString() || data.state,
          postal_code: data.postal_code,
          primary_phone: data.primary_phone,
          fax: data.fax || undefined,
          email: data.email || inviteDetails?.email,
          description: data.description,
          admin_first_name: data.admin_first_name || undefined,
          admin_last_name: data.admin_last_name || undefined,
          password: requiresPassword && !isLinkingExistingPractitioner ? data.password : undefined,
          skipPaymentSetup: true,
          practitionersToInvite: data.practitionersToInvite,
          add_as_practitioner: shouldAddPractitioner,
          practitioner_npi: shouldAddPractitioner ? data.practitioner_npi : undefined,
          practitioner_credentials: shouldAddPractitioner ? data.practitioner_credentials : undefined,
          practitioner_license_number: shouldAddPractitioner ? data.practitioner_license_number : undefined,
          practitioner_dea_number: shouldAddPractitioner ? data.practitioner_dea_number : undefined,
          practitioner_phone: shouldAddPractitioner ? data.practitioner_phone : undefined,
          practitioner_fax: shouldAddPractitioner ? data.practitioner_fax : undefined,
          use_clinic_address_for_practitioner: shouldAddPractitioner ? data.use_clinic_address_for_practitioner : undefined,
          practitioner_address_line_1: shouldAddPractitioner ? data.practitioner_address_line_1 : undefined,
          practitioner_address_line_2: shouldAddPractitioner ? data.practitioner_address_line_2 : undefined,
          practitioner_city: shouldAddPractitioner ? data.practitioner_city : undefined,
          practitioner_state: shouldAddPractitioner ? data.practitioner_state : undefined,
          practitioner_postal_code: shouldAddPractitioner ? data.practitioner_postal_code : undefined,
          existing_practitioner_id: shouldAddPractitioner ? data.existing_practitioner_id : undefined,
          clinic_archetype: data.clinicArchetype || undefined,
          program_acknowledged: true,
        };
        const response = await clinicOnboardingService.completeOnboarding(payload);
        const tokenFromResponse = response.data?.onboardingToken;
        const billingPending = response.data?.billingPending === true;
        if (billingPending && tokenFromResponse) {
          setOnboardingToken(tokenFromResponse);
          setCurrentStep((prev) => prev + 1);
        } else {
          toast({ title: 'Success', description: 'Your clinic has been created.', variant: 'success' });
          await runAutoAuth();
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error?.message || err?.message || 'Failed to complete onboarding';
        toast({ title: 'Error', description: msg, variant: 'destructive' });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const requiresPassword = !inviteDetails?.existingUser;
  const isLinkingExistingPractitioner = Boolean(data.add_as_practitioner && data.existing_practitioner_id);

  const progress = ((currentStep + 1) / visibleSteps.length) * 100;

  const renderStep = () => {
    const stepId = visibleSteps[currentStep]?.id;
    switch (stepId) {
      case 'clinic-info':
        return (
          <ClinicInfoStep
            ref={clinicInfoRef}
            data={data}
            updateData={updateData}
            onValidationChange={() => {}}
            inviteEmail={inviteDetails?.email}
          />
        );
      case 'owner-info':
        return (
          <OwnerInfoStep
            ref={ownerInfoRef}
            data={data}
            updateData={updateData}
            onValidationChange={() => {}}
            inviteDetails={inviteDetails}
            requiresPassword={requiresPassword}
            clinicArchetype={data.clinicArchetype}
          />
        );
      case 'practitioners':
        return <InvitePractitionersStep data={data} updateData={updateData} />;
      case 'acknowledgement':
        return (
          <AcknowledgementStep
            data={data}
            updateData={updateData}
            onAcknowledged={setIsAcknowledged}
          />
        );
      case 'billing':
        return (
          <BillingStep
            allowSkip={true}
            isGuided={effectiveArchetype === 'guided'}
            onSetupIntentRequest={async () => {
              const t = onboardingToken || token;
              if (!t) return null;
              const result = await clinicOnboardingBillingService.createSetupIntent(t);
              return result?.clientSecret ?? null;
            }}
            onPaymentMethodConfirmed={async (paymentMethodId) => {
              const t = onboardingToken || token;
              if (!t) throw new Error('No token');
              await clinicOnboardingBillingService.attachPaymentMethod(t, paymentMethodId);
              setCurrentStep((prev) => prev + 1);
            }}
            onSkip={async () => {
              const t = onboardingToken || token;
              if (!t) return;
              await clinicOnboardingBillingService.skipBilling(t);
              setCurrentStep((prev) => prev + 1);
            }}
          />
        );
      case 'stripe-onboarding':
        return (
          <StripeOnboardingStep
            onAccountSessionRequest={async () => {
              const t = onboardingToken || token;
              if (!t) return null;
              return clinicOnboardingBillingService.createStripeAccountSession(t);
            }}
            onComplete={runAutoAuth}
            onSkip={runAutoAuth}
          />
        );
      default:
        return null;
    }
  };

  const currentStepId = visibleSteps[currentStep]?.id;
  const isBillingStep = currentStepId === 'billing';
  const isStripeOnboardingStep = currentStepId === 'stripe-onboarding';

  // Loading
  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <motion.div className="flex flex-col items-center space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
          <p className="text-gray-500 text-sm">Validating your invitation...</p>
        </motion.div>
      </div>
    );
  }

  // Error
  if (validationError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
        <motion.div className="w-full max-w-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col items-center mb-8">
            <img src="/nimbus_logo.svg" alt="Nimbus Healthcare" className="h-9" />
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-6 w-6 text-red-500" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Invalid Invitation</h1>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">{validationError}</p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-11 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white font-medium shadow-md shadow-brand-teal/20 transition-all"
            >
              Go to Login
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-8 lg:py-12">

          {/* Logo */}
          <motion.div
            className="flex flex-col items-center mb-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <img src="/nimbus_logo.svg" alt="Nimbus Healthcare" className="h-9" />
            <div className="mt-2.5 flex items-center">
              <div className="h-px w-6 bg-gray-300" />
              <span className="mx-2.5 text-[10px] font-semibold text-brand-teal uppercase tracking-[0.15em]">
                Clinic & Practitioner
              </span>
              <div className="h-px w-6 bg-gray-300" />
            </div>
          </motion.div>

          {/* Main Card */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg border border-gray-100/80 overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            {/* Teal gradient banner */}
            <div className="bg-gradient-to-r from-brand-teal to-[#2D6A78] px-6 py-5 sm:px-8 sm:py-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <Building2 className="h-4.5 w-4.5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-white leading-tight">Clinic Onboarding</h1>
                  <p className="text-sm text-white/70 mt-0.5">Complete your clinic setup to get started</p>
                </div>
              </div>

              {/* Stepper */}
              <div className="flex items-center">
                {visibleSteps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isComplete = index < currentStep;
                  const Icon = step.icon;
                  const isLast = index === visibleSteps.length - 1;
                  return (
                    <React.Fragment key={step.id}>
                      <div className="flex items-center gap-2 shrink-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                            isActive
                              ? 'bg-white text-brand-teal shadow-md'
                              : isComplete
                                ? 'bg-white/25 text-white'
                                : 'bg-white/10 text-white/40'
                          }`}
                        >
                          {isComplete ? (
                            <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                          ) : (
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                          )}
                        </div>
                        <span className={`text-xs font-medium whitespace-nowrap hidden sm:inline transition-colors ${
                          isActive ? 'text-white' : isComplete ? 'text-white/70' : 'text-white/40'
                        }`}>
                          {step.title}
                        </span>
                      </div>
                      {!isLast && (
                        <div className={`flex-1 min-w-[8px] h-px mx-1.5 sm:mx-2.5 shrink-0 transition-colors ${
                          isComplete ? 'bg-white/40' : 'bg-white/15'
                        }`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Progress bar */}
              <div className="mt-4 h-1 bg-white/15 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-white rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6 sm:px-8 sm:py-8">
              {/* Invite details + What to Expect on first step */}
              {currentStep === 0 && (
                <div className="space-y-4 mb-6">
                  {inviteDetails && (
                    <motion.div
                      className="rounded-xl border border-gray-100 bg-slate-50 p-4"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className="w-8 h-8 bg-brand-teal/10 rounded-lg flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-brand-teal" />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900">Invitation Details</h3>
                      </div>
                      <div className="space-y-1 ml-[42px]">
                        <p className="text-sm text-gray-500">
                          <span className="font-medium text-gray-700">Organization:</span> {inviteDetails.organisation?.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          <span className="font-medium text-gray-700">Admin Email:</span> {inviteDetails.email}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  <motion.div
                    className="rounded-xl border border-gray-100 bg-slate-50 p-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                        <ClipboardList className="h-4 w-4 text-amber-600" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900">What to Expect</h4>
                    </div>
                    <ol className="text-sm text-gray-500 space-y-1.5 list-decimal list-inside ml-[42px]">
                      <li>Enter clinic and owner information</li>
                      {effectiveArchetype !== 'guided' && (
                        <li>Invite practitioners (optional)</li>
                      )}
                      <li>Acknowledge the program agreement</li>
                      <li>Add a payment method, then sign in</li>
                    </ol>
                  </motion.div>
                </div>
              )}

              {/* Step Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStepId}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                  className="min-h-[400px]"
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>

              {/* Navigation — hidden on billing and Stripe onboarding steps */}
              {!isBillingStep && !isStripeOnboardingStep && (
                <div className="flex items-center justify-between pt-6 border-t border-gray-100 mt-6">
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    disabled={currentStep === 0 || isSubmitting}
                    className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 h-10 px-4 rounded-lg disabled:opacity-0"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1.5" />
                    Back
                  </Button>

                  <Button
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className="h-10 px-6 rounded-lg bg-brand-teal hover:bg-brand-teal/90 text-white font-medium shadow-md shadow-brand-teal/20 hover:shadow-lg hover:shadow-brand-teal/25 transition-all"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
                    ) : (
                      <>Next<ArrowRight className="h-4 w-4 ml-1.5" /></>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Footer */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <div className="flex items-center gap-1.5 text-gray-400 text-xs">
              <Shield className="w-3.5 h-3.5" />
              <span>Secured with enterprise-grade encryption</span>
            </div>
            <p className="text-xs text-gray-400">{getFooterText()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicOnboarding;
